import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import i18n from "../i18n/i18n";
import { getApiUrl, API_ENDPOINTS } from "../config/api";

const CmsContext = createContext({});

/** Skip further requests after a confirmed missing endpoint (avoids 404 spam). */
let cmsContentEndpointMissing = false;

export const CmsContextProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchAndMergeCmsContent = useCallback(async (lang) => {
    if (cmsContentEndpointMissing) return;

    const lng = (lang || i18n.language || "en").toLowerCase().replace("english", "en");
    setLoading(true);
    setError(null);
    try {
      const url = `${getApiUrl(API_ENDPOINTS.CMS_CONTENT_GET)}?lang=${lng}`;
      const res = await axios.get(url, { timeout: 8000 });
      const data = res?.data;
      if (data && typeof data === "object" && Object.keys(data).length > 0) {
        i18n.addResourceBundle(lng, "translation", data, true, true);
      }
    } catch (err) {
      if (err?.response?.status === 404) {
        cmsContentEndpointMissing = true;
      }
      setError(err.message || "CMS content could not be loaded");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAndMergeCmsContent(i18n.language);
  }, [fetchAndMergeCmsContent]);

  useEffect(() => {
    const onLangChanged = (lng) => fetchAndMergeCmsContent(lng);
    i18n.on("languageChanged", onLangChanged);
    return () => i18n.off("languageChanged", onLangChanged);
  }, [fetchAndMergeCmsContent]);

  return (
    <CmsContext.Provider value={{ loading, error, refetch: () => fetchAndMergeCmsContent(i18n.language) }}>
      {children}
    </CmsContext.Provider>
  );
};

export const useCms = () => useContext(CmsContext);
